import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid, TextField,
  InputAdornment, CircularProgress, Stack, Collapse, IconButton, Slide
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import WorkIcon from '@mui/icons-material/Work';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import PaymentIcon from '@mui/icons-material/Payment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { modernTheme, iconBoxSx } from './theme';

const AttendanceForm = ({
  date, onDateChange, hours, onHoursChange, minutes, onMinutesChange,
  loading, onSubmit, userSummary,
}) => {
  const [expanded, setExpanded] = useState(true);

  const hoursNum = parseInt(hours) || 0;
  const minutesNum = parseInt(minutes) || 0;
  const totalHours = hoursNum + (minutesNum / 60);

  const todayDisplay = () => {
    if (hoursNum === 0 && minutesNum === 0) return '0h';
    if (hoursNum > 0 && minutesNum > 0) return `${hoursNum}h${minutesNum}m`;
    if (hoursNum > 0) return `${hoursNum}h`;
    return `${minutesNum}m`;
  };

  const earningPreview = () => {
    if (userSummary && totalHours > 0) {
      const earning = totalHours * userSummary.hourlyRate;
      return `${(earning / 1000).toFixed(0)}K`;
    }
    return '0K';
  };

  return (
    <Slide direction="up" in timeout={800}>
      <Card sx={{
        borderRadius: 3,
        border: `1px solid ${modernTheme.colors.gray[200]}`,
        boxShadow: modernTheme.shadows.md,
        background: modernTheme.gradients.card,
        overflow: 'hidden',
      }}>
        {/* Header - always visible */}
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: { xs: 2, md: 2.5 }, cursor: 'pointer',
            bgcolor: expanded ? 'transparent' : modernTheme.colors.gray[50],
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: modernTheme.colors.gray[50] },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={iconBoxSx(modernTheme.gradients.secondary, 44)}>
              <WorkIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: modernTheme.colors.gray[900], fontWeight: 700, lineHeight: 1.2 }}>
                Chấm công livestream
              </Typography>
              <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500] }}>
                {expanded ? 'Ghi nhận thời gian làm việc' : `Hôm nay: ${todayDisplay()} | Click để mở`}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Collapsible Form */}
        <Collapse in={expanded}>
          <CardContent sx={{ p: { xs: 2, md: 3 }, pt: { xs: 0, md: 0 } }}>
            <Box component="form" onSubmit={onSubmit}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: modernTheme.colors.gray[600], mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                    Ngày livestream
                  </Typography>
                  <DatePicker
                    value={date}
                    onChange={onDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true, size: 'small',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2, bgcolor: 'white',
                            '&:hover fieldset': { borderColor: modernTheme.colors.primary },
                            '&.Mui-focused fieldset': { borderColor: modernTheme.colors.primary, borderWidth: '2px' },
                          },
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: modernTheme.colors.gray[600], mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                    Số giờ
                  </Typography>
                  <TextField
                    fullWidth size="small" type="number"
                    value={hours} onChange={onHoursChange}
                    placeholder="0"
                    inputProps={{ min: 0, max: 24, step: 1, maxLength: 2 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><TimelapseIcon sx={{ color: modernTheme.colors.secondary, fontSize: 18 }} /></InputAdornment>,
                      endAdornment: <InputAdornment position="end"><Typography variant="caption" sx={{ color: modernTheme.colors.gray[500] }}>giờ</Typography></InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2, bgcolor: 'white',
                        '&:hover fieldset': { borderColor: modernTheme.colors.primary },
                        '&.Mui-focused fieldset': { borderColor: modernTheme.colors.primary, borderWidth: '2px' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: modernTheme.colors.gray[600], mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                    Số phút
                  </Typography>
                  <TextField
                    fullWidth size="small" type="number"
                    value={minutes} onChange={onMinutesChange}
                    placeholder="0"
                    inputProps={{ min: 0, max: 59, step: 1, maxLength: 2 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><TimelapseIcon sx={{ color: modernTheme.colors.secondary, fontSize: 18 }} /></InputAdornment>,
                      endAdornment: <InputAdornment position="end"><Typography variant="caption" sx={{ color: modernTheme.colors.gray[500] }}>phút</Typography></InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2, bgcolor: 'white',
                        '&:hover fieldset': { borderColor: modernTheme.colors.primary },
                        '&.Mui-focused fieldset': { borderColor: modernTheme.colors.primary, borderWidth: '2px' },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Quick preview + Submit */}
              <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 200 }}>
                  {[
                    { label: 'Hôm nay', value: todayDisplay(), color: modernTheme.colors.primary },
                    { label: 'Thu nhập', value: earningPreview(), color: modernTheme.colors.success },
                    { label: 'Tỷ lệ', value: userSummary ? `${(userSummary.hourlyRate / 1000)}K/h` : '0K/h', color: modernTheme.colors.warning },
                  ].map((item, i) => (
                    <Box key={i} sx={{
                      flex: 1, py: 1, px: 1.5, textAlign: 'center',
                      bgcolor: modernTheme.colors.gray[50], borderRadius: 2,
                      border: `1px solid ${modernTheme.colors.gray[200]}`,
                    }}>
                      <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500], display: 'block', fontWeight: 600, fontSize: '0.6rem', textTransform: 'uppercase' }}>
                        {item.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: item.color }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                <Button
                  type="submit" variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PaymentIcon />}
                  disabled={loading}
                  sx={{
                    background: loading ? modernTheme.colors.gray[400] : modernTheme.gradients.primary,
                    color: 'white', height: 48, borderRadius: 2,
                    textTransform: 'none', fontWeight: 700,
                    px: { xs: 3, md: 4 },
                    minWidth: { xs: '100%', md: 180 },
                    boxShadow: loading ? 'none' : modernTheme.shadows.md,
                    '&:hover': {
                      background: loading ? modernTheme.colors.gray[400] : modernTheme.gradients.primary,
                      boxShadow: loading ? 'none' : modernTheme.shadows.lg,
                    },
                  }}
                >
                  {loading ? 'Đang xử lý...' : 'Xác nhận'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Collapse>
      </Card>
    </Slide>
  );
};

export default AttendanceForm;
